import classNames from 'classnames';
import React from 'react';
import { FontIconBtn, Tooltip } from '@meitu/saas-ui';
import { TextWritingType } from '@meitu/saas-editor-core/es/version-types';
import { useLocaleStore } from '@/i18n';
import LabelAction from '../label-action';
import './style.less';

export type Features = 'bold' | 'italic' | 'underline' | 'lineThrough' | 'writingMode';

type Props = {
    /**
     * 需要关闭的功能
     */
    closedFeatures?: Features[];

    /**
     * 是否禁用 underline
     */
    disableUnderline?: boolean;

    /**
     * 是否禁用 lineThrough
     */
    disableLineThrough?: boolean;

    /**
     * 是否禁用 writingMode，支持传入 boolean 和 string
     * 若传入 string 类型，则会将其用于禁用时的 tooltip 文案
     */
    disableWritingMode?: boolean | string;

    /**
     * 是否使用 bold 样式
     */
    bold?: boolean;

    /**
     * 是否使用 italic 样式
     */
    italic?: boolean;

    /**
     * 是否使用 underline 文字装饰
     */
    underline?: boolean;

    /**
     * 是否使用 lineThrough 文字装饰
     */
    lineThrough?: boolean;

    /**
     * 文字排版方式
     */
    writingMode?: TextWritingType;

    /**
     * 样式变更回调
     * @param kind - 样式类型
     * @param value - 样式值
     * @returns
     */
    onChange: (kind: Features, value: boolean | string) => void;
};

export const FontStyleGroup: React.FC<Props> = ({
    closedFeatures = [],
    disableUnderline = false,
    disableLineThrough = false,
    disableWritingMode = false,
    bold = false,
    italic = false,
    underline = false,
    lineThrough = false,
    writingMode = TextWritingType.HORIZONTAL_TB,
    onChange,
}) => {
    const { $t } = useLocaleStore();

    const handleChange = (kind: Features, value: boolean | string) => {
        let finalValue = value;

        if (kind === 'writingMode') {
            if (disableWritingMode) return;
            finalValue =
                writingMode === TextWritingType.HORIZONTAL_TB
                    ? TextWritingType.VERTICAL_RL
                    : TextWritingType.HORIZONTAL_TB;
        }

        if (kind === 'lineThrough') {
            if (disableLineThrough) return;
        }

        if (kind === 'underline') {
            if (disableUnderline) return;
        }

        onChange(kind, finalValue);
    };

    return (
        <LabelAction title={$t((v) => v.textAttr.style)} align>
            <div className="font-style-group">
                {/* bold */}
                {closedFeatures.includes('bold') ? null : (
                    <Tooltip placement="bottom" title={$t((v) => v.textAttr.strong)}>
                        <div className="font-style-group__item">
                            <FontIconBtn
                                name="TextBoldBold"
                                fontSize={14}
                                type="button"
                                isActive={bold}
                                onClick={() => handleChange('bold', !bold)}
                            />
                        </div>
                    </Tooltip>
                )}

                {/* italic */}
                {closedFeatures.includes('italic') ? null : (
                    <Tooltip placement="bottom" title={$t((v) => v.textAttr.italic)}>
                        <div className="font-style-group__item">
                            <FontIconBtn
                                name="TextItalicBold"
                                fontSize={14}
                                type="button"
                                isActive={italic}
                                onClick={() => handleChange('italic', !italic)}
                            />
                        </div>
                    </Tooltip>
                )}

                {/* underline */}
                {closedFeatures.includes('underline') ? null : (
                    <Tooltip placement="bottom" title={$t((v) => v.textAttr.underLine)}>
                        <div
                            className={classNames('font-style-group__item', {
                                'font-style-group__item--disabled': disableUnderline,
                            })}
                        >
                            <FontIconBtn
                                name="TextUnderlineBold"
                                fontSize={14}
                                type="button"
                                disable={disableUnderline}
                                isActive={underline}
                                onClick={() => handleChange('underline', !underline)}
                            />
                        </div>
                    </Tooltip>
                )}

                {/* lineThrough */}
                {closedFeatures.includes('lineThrough') ? null : (
                    <Tooltip placement="bottom" title={$t((v) => v.textAttr.lineThrough)}>
                        <div
                            className={classNames('font-style-group__item', {
                                'font-style-group__item--disabled': disableLineThrough,
                            })}
                        >
                            <FontIconBtn
                                name="TextStrikeBold"
                                fontSize={14}
                                type="button"
                                disable={disableLineThrough}
                                isActive={lineThrough}
                                onClick={() => handleChange('lineThrough', !lineThrough)}
                            />
                        </div>
                    </Tooltip>
                )}

                {/* writingMode */}
                {closedFeatures.includes('writingMode') ? null : (
                    <Tooltip
                        placement="bottom"
                        title={
                            typeof disableWritingMode === 'string' && disableWritingMode !== ''
                                ? disableWritingMode
                                : $t((v) => v.textAttr.textVertical)
                        }
                    >
                        <div
                            className={classNames('font-style-group__item', {
                                'font-style-group__item--disabled': Boolean(disableWritingMode),
                            })}
                        >
                            <FontIconBtn
                                name="TextVerticalBold"
                                fontSize={14}
                                type="button"
                                disable={Boolean(disableWritingMode)}
                                isActive={writingMode !== TextWritingType.HORIZONTAL_TB}
                                onClick={() => handleChange('writingMode', '')} // handleChange 会针对 writingMode 进行特殊处理，这里只需要传空字符串即可
                            />
                        </div>
                    </Tooltip>
                )}
            </div>
        </LabelAction>
    );
};
